import { i18n } from "@core/plugins/i18n.plugin"

type EnergyManagerLocale = typeof CONFIG_I18N

let previousDashboardMessages: Partial<
    Record<keyof EnergyManagerLocale, unknown>
> | null = null
let isDashboardRegistered = false

export function registerEnergyManagerDashboardMessages() {
    if (isDashboardRegistered) {
        return
    }

    const currentMessages = i18n.global.getLocaleMessage("fr") as Record<
        string,
        unknown
    >

    previousDashboardMessages = Object.keys(CONFIG_I18N).reduce(
        (acc, key) => {
            acc[key as keyof EnergyManagerLocale] = currentMessages[key]
            return acc
        },
        {} as Partial<Record<keyof EnergyManagerLocale, unknown>>
    )

    i18n.global.mergeLocaleMessage("fr", CONFIG_I18N)

    isDashboardRegistered = true
}

export function unregisterEnergyManagerMessages() {
    if (!isDashboardRegistered) {
        return
    }

    const internalMessages = i18n.global.getLocaleMessage("fr") as Record<
        string,
        unknown
    >
    const currentMessages = { ...internalMessages }

    for (const key of Object.keys(CONFIG_I18N)) {
        const previousValue =
            previousDashboardMessages?.[key as keyof EnergyManagerLocale]

        if (previousValue === undefined) {
            delete currentMessages[key]
            continue
        }

        currentMessages[key] = previousValue
    }

    i18n.global.setLocaleMessage(
        "fr",
        currentMessages as unknown as Parameters<
            typeof i18n.global.setLocaleMessage
        >[1]
    )

    previousDashboardMessages = null
    isDashboardRegistered = false
}

export const CONFIG_I18N = {
    period: "période | périodes",
    unit_of_time: {
        day: "jour | jours",
        week: "semaine | semaines",
        month: "mois",
        year: "an | ans",
    },
    min: "minimum",
    max: "maximum",
    shortcut_period: {
        current_week: "Cette semaine",
        last_7days: "7 derniers jours",
        last_1week: "Semaine dernière",
        last_2weeks: "2 dernières semaines",
        last_4weeks: "4 dernières semaines",
        last_12weeks: "12 dernières semaines",
        last_30days: "30 derniers jours",
        last_1month: "Dernier mois",
        last_3months: "3 derniers mois",
        last_6months: "6 derniers mois",
        last_12months: "12 derniers mois",
        last_1year: "Dernière année",
        last_3years: "3 dernières années",
        last_5years: "5 dernières années",
        custom: "Personnalisée",
    },
    freq: {
        hourly: {
            sentence: "l'horaire",
            long: "Horaire",
        },
        daily: {
            sentence: "le journalier",
            long: "Journalière",
        },
        weekly: {
            sentence: "l'hebdomadaire",
            long: "Hebdomadaire",
        },
        monthly: {
            sentence: "le mensuel",
            long: "Mensuelle",
        },
        yearly: {
            sentence: "l'annuel",
            long: "Annuelle",
        },
    },
    variables: {
        energie_tout_tout: {
            short: "Cons. totale",
            long: "Consommation totale",
            sentence: "La consommation totale",
            sentence_2: "de consommation totale",
            empty_data:
                "que vous avez des compteurs d'électricité ou de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) d'énergie totale dans le parc sélectionné.",
        },
        energie_elec_tout: {
            short: "Cons. élec.",
            long: "Consommation électrique",
            sentence: "La consommation électrique",
            sentence_2: "de consommation électricité",
            empty_data:
                "que vous avez des compteurs d'électricité sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) d'électricité dans le parc sélectionné.",
        },
        energie_gaz_tout: {
            short: "Cons. gaz",
            long: "Consommation de gaz",
            sentence: "La consommation de gaz",
            sentence_2: "de consommation de gaz",
            empty_data:
                "que vous avez des compteurs de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) de gaz dans le parc sélectionné.",
        },
        energie_chaleur_tout: {
            short: "Cons. chaleur",
            long: "Consommation de chaleur",
            sentence: "La consommation de chaleur",
            sentence_2: "de consommation de chaleur",
            empty_data:
                "que vous avez des compteurs de type RCU (réseau de chaleur urbain) sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) de chaleur dans le parc sélectionné.",
        },
        energiepardju_tout_tout: {
            short: "Conso/DJU totale",
            long: "Consommation totale par DJU",
            sentence: "La consommation totale par DJU",
            sentence_2: "de consommation totale par DJU",
            empty_data:
                "que vous avez des compteurs d'électricité ou de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) d'énergie totale par DJU dans le parc sélectionné.",
        },
        energiepardjufroid18_tout_tout: {
            short: "Conso/DJU totale (froid 18°C)",
            long: "Consommation totale par DJU (froid 18°C)",
            sentence: "La consommation totale par DJU (froid 18°C)",
            sentence_2: "de consommation totale par DJU (froid 18°C)",
            empty_data:
                "que vous avez des compteurs d'électricité ou de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) d'énergie totale par DJU froid 18°C dans le parc sélectionné.",
        },
        energiepardju_elec_tout: {
            short: "Conso/DJU élec.",
            long: "Consommation électrique par DJU",
            sentence: "La consommation électrique par DJU",
            sentence_2: "de consommation électricité par DJU",
            empty_data:
                "que vous avez des compteurs d'électricité sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) d'électricité par DJU dans le parc sélectionné.",
        },
        energiepardjufroid18_elec_tout: {
            short: "Conso/DJU élec. (froid 18°C)",
            long: "Consommation électrique par DJU (froid 18°C)",
            sentence: "La consommation électrique par DJU (froid 18°C)",
            sentence_2: "de consommation électricité par DJU (froid 18°C)",
            empty_data:
                "que vous avez des compteurs d'électricité sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) d'électricité par DJU froid 18°C dans le parc sélectionné.",
        },
        energiepardju_gaz_tout: {
            short: "Conso/DJU gaz",
            long: "Consommation de gaz par DJU",
            sentence: "La consommation de gaz par DJU",
            sentence_2: "de consommation de gaz par DJU",
            empty_data:
                "que vous avez des compteurs de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) de gaz par DJU dans le parc sélectionné.",
        },
        energiepardjufroid18_gaz_tout: {
            short: "Conso/DJU gaz (froid 18°C)",
            long: "Consommation de gaz par DJU (froid 18°C) ",
            sentence: "La consommation de gaz par DJU (froid 18°C)",
            sentence_2: "de consommation de gaz par DJU (froid 18°C)",
            empty_data:
                "que vous avez des compteurs de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) de gaz par DJU froid 18°C dans le parc sélectionné.",
        },
        energiepardju_chaleur_tout: {
            short: "Conso/DJU chaleur",
            long: "Consommation de chaleur par DJU",
            sentence: "La consommation de chaleur par DJU",
            sentence_2: "de consommation de chaleur par DJU",
            empty_data:
                "que vous avez des compteurs de type RCU (réseau de chaleur urbain) sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) de chaleur par DJU dans le parc sélectionné.",
        },
        energiepardjufroid18_chaleur_tout: {
            short: "Conso/DJU chaleur (froid 18°C)",
            long: "Consommation de chaleur par DJU (froid 18°C)",
            sentence: "La consommation de chaleur par DJU (froid 18°C)",
            sentence_2: "de consommation de chaleur par DJU (froid 18°C)",
            empty_data:
                "que vous avez des compteurs de type RCU (réseau de chaleur urbain) sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) de chaleur par DJU froid 18°C dans le parc sélectionné.",
        },
        energieparm2_tout_tout: {
            short: "Conso/m² totale",
            long: "Consommation totale par m²",
            sentence: "La consommation totale par m²",
            sentence_2: "de consommation totale par m²",
            empty_data:
                "que vous avez des compteurs d'électricité ou de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) d'énergie totale par m² dans le parc sélectionné.",
        },
        energieparm2_elec_tout: {
            short: "Conso/m² élec.",
            long: "Consommation électrique par m²",
            sentence: "La consommation électrique par m²",
            sentence_2: "de consommation électricité par m²",
            empty_data:
                "que vous avez des compteurs d'électricité sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) d'électricité par m² dans le parc sélectionné.",
        },
        energieparm2_gaz_tout: {
            short: "Conso/m² gaz",
            long: "Consommation de gaz par m²",
            sentence: "La consommation de gaz par m²",
            sentence_2: "de consommation de gaz par m²",
            empty_data:
                "que vous avez des compteurs de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) de gaz par m² dans le parc sélectionné.",
        },
        energieparm2_chaleur_tout: {
            short: "Conso/m² chaleur",
            long: "Consommation de chaleur par m²",
            sentence: "La consommation de chaleur par m²",
            sentence_2: "de consommation de chaleur par m²",
            empty_data:
                "que vous avez des compteurs de type RCU (réseau de chaleur urbain) sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui consomment le plus (+ élevé) et le moins (- élevé) de chaleur par m² dans le parc sélectionné.",
        },
        cout_tout_tout: {
            short: "Coût total",
            long: "Coût total",
            sentence: "Le coût total",
            sentence_2: "de coût total",
            empty_data:
                "que vous avez des compteurs d'électricité ou de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux dont le coût total de consommation est le plus élevé (+ élevé) et le plus bas (- élevé) dans le parc sélectionné.",
        },
        cout_elec_tout: {
            short: "Coût élec.",
            long: "Coût de l'électricité",
            sentence: "Le coût de l'électricité",
            sentence_2: "de coût de l'électricité",
            empty_data:
                "que vous avez des compteurs d'électricité sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux dont le coût d'électricité est le plus élevé (+ élevé) et le plus bas (- élevé) dans le parc sélectionné.",
        },
        cout_gaz_tout: {
            short: "Coût gaz",
            long: "Coût du gaz",
            sentence: "Le coût du gaz",
            sentence_2: "de coût du gaz",
            empty_data:
                "que vous avez des compteurs de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux dont le coût du gaz est le plus élevé (+ élevé) et le plus bas (- élevé) dans le parc sélectionné.",
        },
        eqCO2_tout_tout: {
            short: "eqCO₂ total",
            long: "Équivalent CO₂",
            sentence: "L'équivalent CO₂",
            sentence_2: "d'équivalent CO₂",
            empty_data:
                "que vous avez des compteurs d'électricité ou de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux dont l'empreinte carbone totale (équivalent CO₂) est la plus élevée (+ élevé) et la plus faible (- élevé) dans le parc sélectionné.",
        },
        eqCO2_elec_tout: {
            short: "eqCO₂ élec.",
            long: "Équivalent CO₂ électrique",
            sentence: "L'équivalent CO₂ d'électrique",
            sentence_2: "d'équivalent CO₂ d'électricité",
            empty_data:
                "que vous avez des compteurs d'électricité sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux dont l'empreinte carbone de l'électricité est la plus élevée (+ élevé) et la plus faible (- élevé) dans le parc sélectionné.",
        },
        eqCO2_gaz_tout: {
            short: "eqCO₂ gaz",
            long: "Équivalent CO₂ gaz",
            sentence: "L'équivalent CO₂ de gaz",
            sentence_2: "d'équivalent CO₂ de gaz",
            empty_data:
                "que vous avez des compteurs de gaz sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux dont l'empreinte carbone du gaz est la plus élevée (+ élevé) et la plus faible (- élevé) dans le parc sélectionné.",
        },
        eqCO2_chaleur_tout: {
            short: "eqCO₂ chaleur",
            long: "Équivalent CO₂ chaleur",
            sentence: "L'équivalent CO₂ de chaleur",
            sentence_2: "d'équivalent CO₂ de chaleur",
            empty_data:
                "que vous avez des compteurs de type RCU (réseau de chaleur urbain) sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux dont l'empreinte carbone de la chaleur est la plus élevée (+ élevé) et la plus faible (- élevé) dans le parc sélectionné.",
        },
        temperature_air_interieur: {
            short: "Température intérieure",
            long: "Température intérieure",
            sentence: "La température intérieure",
            sentence_2: "de température intérieure",
            empty_data:
                "que vous avez des capteurs de température sur les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux avec la température intérieure la plus élevée (+ élevé) et la plus basse (- élevé) dans le parc sélectionné.",
        },
        energie_elecinjection_tout: {
            short: "Injection",
            long: "Injection",
            sentence: "L'injection",
            sentence_2: "d'injection",
            empty_data:
                "que vous avez des compteurs d'électricité remontant de l'injection les bâtiments sélectionnés",
            top_flop:
                "Les bâtiments ci-dessus sont ceux qui injectent le plus (+ élevé) et le moins (- élevé) d'électricité dans le réseau dans le parc sélectionné.",
        },
    },
}
